import {
  Avatar,
  Badge,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  tokens,
  Body1,
  Tooltip,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
// ThemeToggle retire en v2 (light only). Cf brandTheme.ts.
// import { ThemeToggle } from './ThemeToggle';
// TenantSwitcher deplace dans Sidebar en v4 :
// import { TenantSwitcher } from './TenantSwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Breadcrumb } from './Breadcrumb';
import { IS_LIVE_MODE, API_BASE_URL } from '../api/liveMode';
import { nis365Api } from '../api/nis365Client';
import { useAuth } from '../auth/AuthContext';

export function TopBar() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Ping /api/health en live pour montrer le status connectivite
  const healthQ = useQuery({
    queryKey:        ['api-health'],
    queryFn:         () => nis365Api.health(),
    enabled:         IS_LIVE_MODE,
    staleTime:       30_000,
    refetchInterval: 60_000,
    retry:           1,
  });

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <header
      style={{
        height:          56,
        borderBottom:    `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        padding:         '0 24px',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'space-between',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
        {/* Fil d Ariane - remplit l espace vide a gauche de la TopBar (v7). */}
        <Breadcrumb />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <LanguageSwitcher />
        <div
          style={{
            width:           1,
            height:          24,
            backgroundColor: tokens.colorNeutralStroke2,
            margin:          '0 4px',
          }}
        />
        {isAuthenticated && user ? (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<Avatar name={user.name ?? user.email} size={28} />}
              >
                {user.name ?? user.email}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => navigate('/settings')}>
                  {t('topbar.myAccount')}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  {t('topbar.signOut')}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : import.meta.env.MODE === 'development' ? (
          // En dev local on garde un mini badge pour reperer le mode courant.
          // En prod (deploye sur app.nis365.eu) on n affiche rien dans la TopBar
          // quand non authentifie - le helper "Mode demo / API live (dev sans auth)"
          // etait un reliquat dev qui leakait sur la page publique.
          <Body1 style={{ color: tokens.colorNeutralForeground3, fontSize: 12 }}>
            {IS_LIVE_MODE ? '(dev/live)' : '(dev/demo)'}
          </Body1>
        ) : null}
      </div>
    </header>
  );
}

// ----------------------------------------------------------------------------
// Indicateur de mode (demo / live + status connectivite API)
// ----------------------------------------------------------------------------

interface ModeIndicatorProps {
  isLive:    boolean;
  apiUrl:    string;
  isHealthy: boolean;
  isLoading: boolean;
  isError:   boolean;
}

function ModeIndicator({ isLive, apiUrl, isHealthy, isLoading, isError }: ModeIndicatorProps) {
  if (!isLive) {
    return (
      <Tooltip
        content="Donnees mockees stockees en localStorage. Set VITE_API_BASE_URL pour passer en live."
        relationship="label"
        withArrow
      >
        <Badge
          appearance="filled"
          style={{
            backgroundColor: '#FAEDD9',
            color:           '#A86317',
            fontWeight:      600,
          }}
        >
          DEMO
        </Badge>
      </Tooltip>
    );
  }

  const color = isLoading
    ? { bg: '#F1F3F5', fg: '#6B7280', label: 'CONNEXION...' }
    : isError || !isHealthy
    ? { bg: '#FBE7E7', fg: '#B82329', label: 'API HORS LIGNE' }
    : { bg: '#E6F4EC', fg: '#1F7A45', label: 'LIVE' };

  return (
    <Tooltip content={`API : ${apiUrl}`} relationship="label" withArrow>
      <Badge
        appearance="filled"
        style={{
          backgroundColor: color.bg,
          color:           color.fg,
          fontWeight:      600,
        }}
      >
        {color.label}
      </Badge>
    </Tooltip>
  );
}
